import { call, select } from 'redux-saga/effects';
import { SFrameManager, SFrameTornDownError } from 'features/e2ee/SFrameManager';
import { User } from 'features/users/types';
import { selectUserById } from 'features/users/usersSlice';
import { E2EEManager } from 'features/e2ee/E2EEManager';
import { RTCClient } from 'utils/webrtc';
import { refineSframeStatus } from 'features/e2ee/sagas/refineSframeStatus';
import { E2eeReceiver } from 'features/e2ee/E2eeReceiver';
import { remoteStreamSecretDecrypted } from 'features/e2ee/actions';
import { logger } from 'utils/logger';
import * as Sentry from '@sentry/react';

export function* onRemoteStreamSecretDecryptedSaga(
  action: ReturnType<typeof remoteStreamSecretDecrypted>
) {
  const user: User = yield select(selectUserById, action.payload.userId);

  if (user) {
    yield call(refineSframeStatus);

    const receiver: E2eeReceiver | undefined = yield call(
      E2EEManager.getReceiver,
      user.id,
      action.payload.roomId
    );

    if (receiver?.streamKey) {
      try {
        // e2eeId is late-bound: unavailable during waiting room / breakout room
        // key exchange (publicKeyShared), populated after roomJoined/userJoined
        yield call(E2EEManager.updateReceiverE2eeId, user.id, action.payload.roomId, user.e2eeId!);

        // Add receiver and wait for crypto initialization to complete
        yield call(SFrameManager.addReceiver, user.e2eeId!, receiver.streamKey);
        yield call(SFrameManager.waitForReceiverReady, user.e2eeId!);

        yield call(RTCClient.receivingFeed.subscribeToE2eeUser, user.id);
      } catch (error) {
        if (error instanceof SFrameTornDownError) {
          logger
            .remote({ system: true, capture: 'e2ee' })
            .info('[E2EE] Receiver setup aborted: session torn down', { userId: user.id });

          return;
        }

        const message = `[E2EE] Failed to setup receiver for userId=${user.id}`;
        logger.remote({ system: true, capture: 'e2ee' }).error(message, {
          e2eeId: user.e2eeId,
          error: error instanceof Error ? error.message : JSON.stringify(error),
        });
        Sentry.captureException(error);
      }
    }
  }
}
